// H-A3 (Track H Wave A) — the kid's persistent chrome, on H-0 tokens.
//
// Design intent: a calm cream-deep "stone" bar lifted off the page with
// shadow-stone; the active tab reads terracotta (the brand accent) via a
// solid icon pill, while the LABEL stays ink so 12px text keeps AA
// contrast (terracotta on cream-deep is only ~3:1 — fine for the icon
// pill as a UI graphic, not for small text). Inactive tabs use
// muted-foreground (AA-safe on cream-deep post-H-0, 5.25:1).
//
// Frozen out of this lane: TAB_BAR_PATHS gating lives in App.jsx (lane
// A5 owns it). Labels are asserted by e2e (15-nav-shell) via accessible
// link names — "Practice" / "Read", exact.
//
// MA-328: the /play tab reads "Practice" (was "Storybook" — the surface
// it opens is the practice path, not a library). /library also renders
// this bar (folded into the Read tab, see MA-148(d) below) and shows its
// own locked-book caption "Practice to earn it" — any FUTURE non-exact
// text locator on /library risks matching both; existing tests correctly
// use exact accessible-name matches on the tab link.
//
// MA-148(d): /library (the story-browse screen, App.jsx TAB_BAR_PATHS)
// is a child screen of the Read flow (ReadTab links into it, its
// onBack returns to /read) but isn't nested under /read in the URL —
// fold it into the "Read" tab's active check so it doesn't render with
// no active tab at all.
//
// MA-342: the "Pal" tab is RETIRED — a whole top-level tab for an
// occasional cosmetic swap (first-pick already lives in onboarding) had
// no comparable precedent in kids' apps. /pal survives as a route
// (PathHome's header pal is now the tappable "switch your pal" chip,
// data-testid="pal-chip") but is no longer a hub surface, so it's also
// dropped from App.jsx's TAB_BAR_PATHS — there is no third tab left to
// fold a visit to /pal into.
//
// MA-343: the third slot is now "Journey" (kid-IA D2/D3) — the honest,
// fold-derived chapter path at /journey (a top-level peer of /play and
// /read, not a /play sub-route). Order per the converged design memo:
// Practice · Read · Journey.
//
// MA-894 (KID-JOURNEY-2026-10-08 D-KJ-14): the third tab is the badge book.
// It reads "Badges" with a medal icon (the route stays /journey, so links and
// the grown-ups entry keep working), and carries a gold dot while the child
// holds a badge record they have not seen in the book yet, re-read on every
// route change so the dot clears once /journey has been opened.
// RED SHOULD-1 (operator decision D-1019-20): the dot is derived from LOCAL
// stores only — the badge_earned latch rows and session_completed markers in the
// local milestone mirror, minus the MA-892 seen store (unseenBadgeRecordCount).
// The tab bar never mounts useBadges, so /play, /read and /library fetch no
// attempt or event history for it and make no badge writes; the full book is
// computed on /journey only.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { BookOpen, Library, Medal } from "lucide-react";
import { useAppState } from "@/App/AppStateContext.tsx";
import { unseenBadgeRecordCount } from "@/lib/engine/badges/tabDot";
import { BADGES_TAB_LABEL } from "@/lib/engine/badgeBookSpeak";
import { JOURNEY_PICK_ADDED_EVENT, type JourneyPickAddedEventDetail } from "@/lib/engine/journeyPickFeedback";
import { motionAllowed } from "@/lib/motion";

const tabs = [
  { to: "/play", label: "Practice", icon: BookOpen },
  { to: "/read", label: "Read", icon: Library },
  { to: "/journey", label: BADGES_TAB_LABEL, icon: Medal },
] as const;

// MA-368 (scenario-backfill lane, ma-342 slice, DOM-hook-only): an answer-safe
// probe for a future live-QA scenario asserting the retired "/pal" tab never
// reappears in the bottom nav (MA-342). Route paths only — never a label/icon,
// so this can never leak kid-facing content, mirroring data-trace-cta's
// id-not-text convention. Derived from `tabs` itself (never hand-duplicated)
// so it can't silently drift from the real rendered tab set.
const NAV_TAB_ROUTES = tabs.map((t) => t.to).join(",");

// MA-497 (Journey v3, PR-B / §5 + A12) — a picked stone's flying-dot arc
// lands here (the Practice tab icon), which pulses once. This component is
// always mounted whenever /journey is (the persistent bottom-tab shell),
// so the listener is live for every pick. Reduced motion: NO dot is
// spawned at all (a static half-flown dot would read as a stray mark, not
// a calm equivalent) and the pulse still fires (its own CSS class is
// `animation: none` under reduced motion via globals.css, so this is
// already the "static swap" — the icon's normal at-rest look, no visible
// motion, no compensating substitute needed).
const FLY_DOT_DURATION_MS = 560;
const PULSE_DURATION_MS = 520;

export function BottomTabBar() {
  const { pathname } = useLocation();
  const practiceIconRef = useRef<HTMLSpanElement | null>(null);
  const [pulsing, setPulsing] = useState(false);
  const [flyDot, setFlyDot] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  // MA-894 — the Badges tab's gold dot.
  const app = useAppState();
  const badgeChildId: string | null = app?.activeChildId ?? null;
  const onBook = pathname === "/journey" || pathname.startsWith("/journey/");
  const unseenBadges = useMemo(
    () => (onBook ? 0 : unseenBadgeRecordCount(badgeChildId)),
    // pathname: the local stores are re-read on every route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [badgeChildId, onBook, pathname],
  );

  useEffect(() => {
    function onPickAdded(e: Event) {
      const detail = (e as CustomEvent<JourneyPickAddedEventDetail>).detail;
      if (!detail || !practiceIconRef.current) return;
      setPulsing(true);
      window.setTimeout(() => setPulsing(false), PULSE_DURATION_MS);
      if (motionAllowed()) {
        const target = practiceIconRef.current.getBoundingClientRect();
        setFlyDot({
          x0: detail.x,
          y0: detail.y,
          x1: target.left + target.width / 2,
          y1: target.top + target.height / 2,
        });
        window.setTimeout(() => setFlyDot(null), FLY_DOT_DURATION_MS);
      }
    }
    window.addEventListener(JOURNEY_PICK_ADDED_EVENT, onPickAdded);
    return () => window.removeEventListener(JOURNEY_PICK_ADDED_EVENT, onPickAdded);
  }, []);

  return (
    <nav
      aria-label="Main"
      data-nav-tab-routes={NAV_TAB_ROUTES}
      className="w-full px-4 pb-[max(10px,env(safe-area-inset-bottom))] pt-2"
    >
      <div className="flex items-stretch justify-around gap-1 rounded-[28px] border border-border bg-cream-deep px-2 py-1.5 shadow-stone">
        {tabs.map((t) => {
          const active =
            t.to === "/play"
              ? pathname === "/play" || pathname.startsWith("/play/")
              : t.to === "/journey"
                ? pathname === "/journey" || pathname.startsWith("/journey/")
                : pathname === "/read" ||
                  pathname.startsWith("/read/") ||
                  pathname === "/library" ||
                  pathname.startsWith("/library/");
          const Icon = t.icon;
          return (
            <Link
              key={t.to}
              to={t.to}
              aria-current={active ? "page" : undefined}
              // MA-148 closure instrument (served-surface-capture, affordance
              // d): INERT hook mirroring the `active` boolean this component
              // ALREADY computes — no new logic. ma148NavActiveState.mjs does
              // NOT trust this attribute's value alone: it independently
              // scans document-wide for the single [data-nav-active="1"]
              // element and checks it ALSO carries aria-current="page" AND a
              // real nested <svg> icon (per the MA-647 lesson).
              data-nav-active={active ? "1" : "0"}
              className={`flex min-h-[56px] min-w-[44px] flex-1 flex-col items-center justify-center gap-1 rounded-3xl px-2 py-1.5 transition-[color,transform] duration-150 active:scale-[0.94] ${
                active
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span
                ref={t.to === "/play" ? practiceIconRef : undefined}
                aria-hidden="true"
                data-testid={t.to === "/play" ? "practice-tab-icon" : undefined}
                className={`relative grid h-7 w-12 place-items-center rounded-full transition-colors ${
                  active ? "bg-terracotta text-primary-foreground" : ""
                } ${t.to === "/play" && pulsing ? "animate-tab-pulse" : ""}`}
              >
                <Icon size={19} strokeWidth={active ? 2.4 : 2} />
                {t.to === "/journey" && unseenBadges > 0 && (
                  <span
                    aria-hidden="true"
                    data-testid="badges-tab-dot"
                    className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-gold"
                    style={{ boxShadow: "0 0 0 2px var(--cream-deep)" }}
                  />
                )}
              </span>
              <span className="text-xs font-bold leading-none">{t.label}</span>
            </Link>
          );
        })}
      </div>
      {/* MA-497 (§5/A12) — the flying dot: fixed-position, geometry passed
          via inline CSS custom properties (the two measured rects), never
          spawned under reduced motion (see the effect above). */}
      {flyDot && (
        <span
          aria-hidden="true"
          data-testid="pick-fly-dot"
          className="animate-pick-fly-dot pointer-events-none fixed left-0 top-0 z-[70] h-2.5 w-2.5 rounded-full bg-terracotta"
          style={
            {
              "--pick-fly-x0": `${flyDot.x0}px`,
              "--pick-fly-y0": `${flyDot.y0}px`,
              "--pick-fly-x1": `${flyDot.x1}px`,
              "--pick-fly-y1": `${flyDot.y1}px`,
            } as React.CSSProperties
          }
        />
      )}
    </nav>
  );
}
